import {
  createDatabaseBlockDataSource,
  isDatabaseStorageModelReady,
  isHiddenFileIndexDatabase,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine/model';
import type { Store } from '@blocksuite/affine/store';

export interface DatabaseSelectionCandidate {
  id: string;
  model: unknown;
  title?: string;
  titleLoading?: boolean;
}

function readModelTitle(model: unknown): string {
  const props = (model as { props?: { title?: { toString(): string } } })
    ?.props;
  return props?.title?.toString().trim() || 'Untitled';
}

function scoreDatabaseModel(model: unknown): {
  readonly title: string;
  readonly score: number;
} {
  try {
    if (!isDatabaseStorageModelReady(model as never)) {
      return { title: readModelTitle(model), score: -1 };
    }

    // The source observes its root until disposed; the score only reads it.
    const dataSource = createDatabaseBlockDataSource(model as never);
    try {
      const storageModel = dataSource.storageModel as {
        props?: {
          title?: { toString(): string };
          ['columns$']?: { value?: unknown[] };
        };
      };
      const title =
        storageModel.props?.title?.toString().trim() || readModelTitle(model);
      const columnCount = storageModel.props?.['columns$']?.value?.length ?? 0;
      const rowCount = (dataSource as { ['rowCount$']: { value: number } })[
        'rowCount$'
      ].value;
      return {
        title: title || 'Untitled',
        score: rowCount * 1000 + columnCount,
      };
    } finally {
      dataSource.dispose();
    }
  } catch {
    return { title: readModelTitle(model), score: -1 };
  }
}

function normalizeTitle(value: string): string {
  return value.trim().toLocaleLowerCase();
}

export function orderCanonicalDatabaseCandidates<
  T extends DatabaseSelectionCandidate,
>(candidates: readonly T[]): T[] {
  const ranked = candidates.map((candidate, index) => {
    const score = scoreDatabaseModel(candidate.model);
    const title =
      candidate.title && !candidate.titleLoading
        ? candidate.title
        : score.title;
    return {
      candidate,
      index,
      key: normalizeTitle(title),
      score: score.score,
    };
  });

  ranked.sort((a, b) => {
    if (a.key !== b.key) {
      return a.index - b.index;
    }
    return b.score - a.score || a.index - b.index;
  });

  return ranked.map(entry => entry.candidate);
}

function trashedDatabaseIds(doc: Store): Set<string> {
  const trashcan = doc.getBlocksByFlavour('affine:trashcan')[0];
  const items = (
    trashcan?.model.props as { items?: { id: string; blockType: string }[] }
  )?.items;
  return new Set(
    (items ?? [])
      .filter(item => item.blockType === 'database')
      .map(item => item.id)
  );
}

/**
 * Databases a user can open, in document order: hidden system tables such as
 * the File Index and databases in the trashcan are not part of it.
 */
export function listUserDatabaseBlocks(
  doc: Store
): { id: string; model: DatabaseBlockModel }[] {
  const root = doc.root;
  if (!root) return [];
  const trashedIds = trashedDatabaseIds(doc);
  const databases: { id: string; model: DatabaseBlockModel }[] = [];
  const traverse = (blockId: string) => {
    const block = doc.getBlock(blockId);
    if (!block) return;
    if (block.flavour === 'affine:database') {
      const model = block.model as DatabaseBlockModel;
      if (!trashedIds.has(blockId) && !isHiddenFileIndexDatabase(model)) {
        databases.push({ id: blockId, model });
      }
    }
    for (const child of block.model.children) traverse(child.id);
  };
  traverse(root.id);
  return databases;
}
